<template>
  <modal
    v-if="show"
    height="720px"
    width="560px"
    bodyPadding="0"
    :title="t('Exchange Rates For Currencies')"
    @close="close"
    :read-article-url="`https://docs.bemob.com/${i18n.global.locale.value}/currencies-settings`"
  >
    <div class="content-container">
      <div class="currency-source">
        {{
          t(
            'Exchange rates are delivered from financial data providers and banks, including the ECB.',
          )
        }}
      </div>
      <div class="currency-container">
        {{ `${t('Account Currency')}:` }}
        <div class="currency">
          <span class="currency-symbol">{{ currencySymbol(currencyId) }}</span>
          {{ currencyId }}
        </div>
      </div>
      <div class="table">
        <div class="header">
          <div>{{ t('Currency') }}</div>
          <div>{{ t('Code') }}</div>
          <div>{{ t('Value') }}</div>
        </div>
        <div class="body">
          <div class="row" v-for="rate in filteredRates" :key="rate.toCurrency">
            <div>{{ t(currencyMap[rate.toCurrency]) || rate.toCurrency }}</div>
            <div>{{ `${currencySymbol(rate.toCurrency)} ${rate.toCurrency}` }}</div>
            <div>{{ fixedDecimals(rate.rate) }}</div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="d-flex justify-end">
        <ui-button
          :text="t('Close')"
          icon="fas fa-check"
          button-style="accent"
          :loading="loading"
          @click="close"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import type { Currency, User } from '@/services/models';
import {
  getExchangeRatesApiRequest,
  type CurrencyExchangeRate,
} from '@/services/api/currency-api';
import currencyList from '@/components/helpers/currency-list';
import i18n from '@/i18n';

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const store = useStore();
const show = ref(false);
const loading = ref(false);
const rates = ref<CurrencyExchangeRate[]>([]);

const currencyId = computed(() => (store.user as User).currencyId);

const currencyMap = computed(() =>
  store.currencyList.reduce((acc: Record<string, string>, item: Currency) => {
    acc[item.code] = item.name;
    return acc;
  }, {}),
);

const filteredRates = computed(() => {
  const preferredCurrency = (store.user as User).preferredCurrency ?? [];
  return rates.value
    .filter(({ toCurrency }: CurrencyExchangeRate) => toCurrency !== currencyId.value)
    .sort(
      (a: CurrencyExchangeRate, b: CurrencyExchangeRate) =>
        preferredCurrency.indexOf(b.toCurrency) - preferredCurrency.indexOf(a.toCurrency),
    );
});

const fixedDecimals = (num: number) => {
  return Math.round(num * 100) / 100;
};

const currencySymbol = (id: string) => {
  return currencyList[id as keyof typeof currencyList];
};

const close = () => {
  emit('close');
};

onMounted(async () => {
  loading.value = true;
  try {
    rates.value = await getExchangeRatesApiRequest(currencyId.value);
  } finally {
    loading.value = false;
    show.value = true;
  }
});
</script>

<style lang="scss" scoped>

.content-container {
  height: 100%;
  flex-direction: column;
}

.currency-container {
  display: flex;
  font-size: 16px;
  padding: 15px 25px 15px;
  text-transform: capitalize;
  color: var(--color-graphite);

  .currency {
    margin-left: 5px;
    font-weight: bold;
  }

  .currency-symbol {
    color: var(--color-graphite);
  }
}

.currency-source {
  padding: 24px 25px 0;
}

.table {
  width: 100%;
  height: 100%;
  color: var(--color-graphite);
  .body {
    max-height: calc(100% - 128px);
    overflow-y: auto;
    padding: 0 0 0 25px;
  }

  .header {
    margin: 0 25px;
    font-weight: bold;
    border-bottom: 2px solid var(--color-gray10);
  }

  .row {
    &:not(:last-child) {
      border-bottom: 1px solid var(--color-gray10);
    }
  }

  .header,
  .row {
    width: calc(570px - 60px);
    @media all and (max-width: 570px) {
      width: calc(100vw - 60px);
    }
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 1fr) minmax(0, 2fr);
    font-size: 13px;
    padding: 5px 0;
    word-break: break-all;

    div {
      &:nth-child(1) {
        word-break: break-word;
        padding-right: 15px;
      }
      &:nth-child(2) {
        white-space: nowrap;
        padding-right: 15px;
      }
      &:nth-child(3) {
        text-align: right;
      }
    }
  }
}
</style>
